import { useState, useMemo } from "react";
import { Formik, Form } from "formik";
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Typography,
} from "@material-tailwind/react";
import MaterialIcon from "components/material-icon";
import FormikCheckbox from "components/form/formik-checkbox";
import AddNewThemeDialog from "./add-new-theme";
import EditThemeDialog from "./edit-theme";
import { sortThemesByName } from "../utils/sort-themes";
import { fetcherFull } from "utils/api";
import { toast } from "react-toastify";

export default function AddToThemeDialog({
    open,
    onClose,
    themes = [], // [{ id, name }]
    responses = [],
    onAddToTheme, // (selectedThemeIds, responses) => {}
    surveyId,
    themesDataMutate,
    handleEditThemeName,
}) {
    const handleClose = () => {
        onClose();
    };
    const [selectedThemes, setSelectedThemes] = useState([]);
    const [showAddNewThemeDialog, setShowAddNewThemeDialog] = useState(false);
    const [showEditThemeDialog, setShowEditThemeDialog] = useState(false);
    const [editingTheme, setEditingTheme] = useState(null);

    const sortedThemes = useMemo(() => sortThemesByName(themes), [themes]);

    const toggleThemeSelection = (id) => {
        setSelectedThemes((prev) =>
            prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
        );
    };

    const handleAdd = () => {
        if (selectedThemes.length > 0) {
            onAddToTheme(selectedThemes, responses);
            setSelectedThemes([]);
            onClose();
        }
    };

    const handleAddNewTheme = async (newThemeName) => {
        const themeObj = {
            name: newThemeName,
        };
        let response = undefined;
        try {
            response = await fetcherFull(
                `/v2/survey/${surveyId}/theme`,
                "POST",
                themeObj,
            );
        } catch (err) {
            const serverError = err?.message || "Unknown issue creating Theme.";
            toast.error(`Error creating Theme, ${serverError}.`);
            setSelectedThemes([]);
            return;
        }
        if (response && response?.name) {
            themesDataMutate();
            toast.success("Theme Created");
        } else {
            const serverError = response || "Unknown issue creating Theme.";
            toast.error(`Error creating Theme, ${serverError}`);
        }
        setSelectedThemes([]);
    };

    return (
        <>
            {/* Parent AddToThemeDialog */}
            <Dialog
                open={open && !showAddNewThemeDialog && !showEditThemeDialog}
                handler={handleClose}
                className="dialog"
            >
                <DialogHeader className="dialog__header flex items-center gap-2">
                    <Typography variant="h4">Add to Theme</Typography>
                </DialogHeader>

                <Formik
                    initialValues={sortedThemes.reduce(
                        (acc, theme) => ({ ...acc, [theme.id]: false }),
                        {},
                    )}
                    onSubmit={handleAdd}
                >
                    {() => (
                        <Form>
                            <DialogBody className="dialog__body">
                                {/* Add New Theme Button */}
                                <div className="dialog__section">
                                    <Button
                                        type="button"
                                        className="btn btn--primary flex items-center gap-2"
                                        onClick={() =>
                                            setShowAddNewThemeDialog(true)
                                        }
                                    >
                                        <MaterialIcon name="add" />
                                        <span>New</span>
                                    </Button>
                                </div>

                                {/* Scrollable List of Themes */}
                                <div className="dialog__section max-h-64 overflow-y-auto space-y-2">
                                    {sortedThemes.map((theme) => (
                                        <div
                                            key={theme.id}
                                            className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-md"
                                        >
                                            <FormikCheckbox
                                                name={theme.id.toString()}
                                                label={theme.name}
                                                labelProps={{
                                                    className:
                                                        "text-primary font-body",
                                                }}
                                                className="text-primary font-body"
                                                checked={selectedThemes.includes(
                                                    theme.id,
                                                )}
                                                onChange={() =>
                                                    toggleThemeSelection(
                                                        theme.id,
                                                    )
                                                }
                                            />
                                            <Button
                                                type="button"
                                                className="btn-icon--secondary"
                                                onClick={() => {
                                                    setEditingTheme(theme);
                                                    setShowEditThemeDialog(
                                                        true,
                                                    );
                                                }}
                                            >
                                                <MaterialIcon name="edit" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </DialogBody>

                            <DialogFooter className="dialog__footer">
                                <Button
                                    type="button"
                                    className="btn btn--danger"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    className="btn btn--primary"
                                    disabled={selectedThemes.length === 0}
                                >
                                    Add to Theme
                                </Button>
                            </DialogFooter>
                        </Form>
                    )}
                </Formik>
            </Dialog>

            {/* Add New Theme Dialog */}
            <AddNewThemeDialog
                open={showAddNewThemeDialog}
                onClose={() => setShowAddNewThemeDialog(false)}
                onAddNewTheme={(name) => {
                    handleAddNewTheme(name);
                    setShowAddNewThemeDialog(false);
                }}
            />

            {/* Edit Theme Dialog */}
            {editingTheme && (
                <EditThemeDialog
                    open={showEditThemeDialog}
                    theme={editingTheme}
                    onClose={() => setShowEditThemeDialog(false)}
                    onSaveThemeName={(id, newName) => {
                        handleEditThemeName(id, newName);
                        setShowEditThemeDialog(false);
                    }}
                />
            )}
        </>
    );
}
